import jsPDF from "jspdf";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { BRAND } from "@/config/brand";
import logoImage from "@/assets/regard-de-nature-logo.png";
import notoSansRegularUrl from "@/assets/fonts/NotoSans-Regular.ttf";
import notoSansBoldUrl from "@/assets/fonts/NotoSans-Bold.ttf";
import { IridologyFormData, IrisPhoto, IridologyRecommendation } from "@/types/iridology";
import { IrisCalibration, IrisZone } from "@/components/iridology/IrisInteractiveMap";
import { IridologyObservationsData } from "@/components/iridology/IridologyObservationsForm";

interface EyeData {
  imageData: string | null;
  calibration: IrisCalibration | null;
}

interface ZoneObservation {
  id?: string;
  zone_id: string;
  zone_label: string;
  zone_ring: string;
  eye_side: string;
  observation_notes: string | null;
  severity_level: string | null;
  is_highlighted?: boolean;
}

export interface SelectedZoneWithDetails {
  zone: IrisZone;
  eyeSide: "D" | "G";
  observation?: string;
  severityLevel?: string;
}

export interface FullSessionPdfData {
  patientName: string;
  sessionDate: string;
  formData: IridologyFormData;
  photos: IrisPhoto[];
  recommendations?: IridologyRecommendation[];
  rightEye?: EyeData;
  leftEye?: EyeData;
  zoneObservations?: ZoneObservation[];
  selectedZones?: SelectedZoneWithDetails[];
  signatureUrl?: string;
  observationsData?: IridologyObservationsData;
  practitionerInfo?: {
    practitioner_name?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
}

export const generateFullSessionPdf = async (data: FullSessionPdfData) => {
  const {
    patientName,
    sessionDate,
    formData,
    photos,
    recommendations = [],
    rightEye,
    leftEye,
    zoneObservations = [],
    selectedZones = [],
    observationsData,
    practitionerInfo,
  } = data;

  const doc = new jsPDF({ unit: "mm", format: "a4" });

  // ---- Embed a real Unicode TTF font (fix accents/ligatures/spacing) ----
  const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  };

  const loadFontBase64 = async (url: string) => {
    const res = await fetch(url);
    const buf = await res.arrayBuffer();
    return arrayBufferToBase64(buf);
  };

  const ensurePdfFonts = async () => {
    const anyDoc = doc as any;
    if (anyDoc.__notoSansLoaded) return;

    const [regularB64, boldB64] = await Promise.all([
      loadFontBase64(notoSansRegularUrl),
      loadFontBase64(notoSansBoldUrl),
    ]);

    doc.addFileToVFS("NotoSans-Regular.ttf", regularB64);
    doc.addFileToVFS("NotoSans-Bold.ttf", boldB64);

    doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");
    doc.addFont("NotoSans-Regular.ttf", "NotoSans", "italic");
    doc.addFont("NotoSans-Bold.ttf", "NotoSans", "bold");
    doc.addFont("NotoSans-Bold.ttf", "NotoSans", "bolditalic");

    // Map all existing helvetica calls to NotoSans without rewriting the whole file
    const originalSetFont = doc.setFont.bind(doc);
    (doc as any).setFont = (fontName: any, fontStyle?: any) => {
      const mappedName = fontName === "helvetica" ? "NotoSans" : fontName;
      return originalSetFont(mappedName, fontStyle);
    };

    doc.setFont("NotoSans", "normal");
    anyDoc.__notoSansLoaded = true;
  };

  const resolveImageToDataUrl = async (src: string) => {
    if (!src) return null;
    if (src.startsWith("data:")) return src;

    const res = await fetch(src);
    const blob = await res.blob();

    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  };

  const dataUrlFormat = (dataUrl: string) =>
    dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";

  const renderEyeWithSelectedZones = async (
    imageDataUrl: string,
    calibration: IrisCalibration | null | undefined,
    zonesForEye: SelectedZoneWithDetails[]
  ) => {
    if (!imageDataUrl || !calibration) return imageDataUrl;

    const img = new Image();
    img.src = imageDataUrl;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Image load failed"));
    });

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return imageDataUrl;

    ctx.drawImage(img, 0, 0);

    const centerX = calibration.centerX;
    const centerY = calibration.centerY;
    const pupilRadius = calibration.pupilRadius;
    const irisRadius = calibration.irisRadius;
    const innerRingOuterRadius = pupilRadius + (irisRadius - pupilRadius) * 0.4;

    const drawSector = (zone: IrisZone) => {
      const startRad = ((zone.startAngle - 90) * Math.PI) / 180;
      const endRad = ((zone.endAngle - 90) * Math.PI) / 180;

      const isInner = zone.ring === "inner";
      const innerR = isInner ? pupilRadius : innerRingOuterRadius;
      const outerR = isInner ? innerRingOuterRadius : irisRadius;

      ctx.beginPath();
      ctx.arc(centerX, centerY, outerR, startRad, endRad);
      ctx.arc(centerX, centerY, innerR, endRad, startRad, true);
      ctx.closePath();

      const hex = zone.color || "#3B82F6";
      const r = parseInt(hex.slice(1, 3), 16) || 59;
      const g = parseInt(hex.slice(3, 5), 16) || 130;
      const b = parseInt(hex.slice(5, 7), 16) || 246;

      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.28)`;
      ctx.fill();
      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.9)`;
      ctx.lineWidth = Math.max(2, Math.round(img.naturalWidth / 600));
      ctx.stroke();
    };

    zonesForEye.forEach((z) => drawSector(z.zone));

    // NO calibration circles drawn in PDF — clean photo output

    return canvas.toDataURL("image/jpeg", 0.92);
  };

  await ensurePdfFonts();

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  const lineHeight = 5;
  let y = margin;
  let pageNum = 1;

  // Brand colors
  const colors = {
    primary: BRAND.colors.rgb.forestGreen,
    secondary: BRAND.colors.rgb.softAccent,
    accent: BRAND.colors.rgb.gold,
    background: BRAND.colors.rgb.lightBackground,
    text: BRAND.colors.rgb.text,
    textLight: BRAND.colors.rgb.textLight,
    white: BRAND.colors.rgb.white,
  };

  const severityColors: Record<string, { r: number; g: number; b: number }> = {
    normal: { r: 34, g: 139, b: 34 },
    attention: { r: 218, g: 165, b: 32 },
    priority: { r: 200, g: 80, b: 80 },
  };

  const priorityLabels: Record<string, string> = {
    normal: "Normal",
    attention: "A surveiller",
    priority: "Prioritaire",
  };

  // ==================== HELPERS ====================
  const footerHeight = 30; // Reserve space for footer
  const safeBottomY = pageHeight - footerHeight;

  const checkPageBreak = (extraSpace: number = 20) => {
    if (y + extraSpace > safeBottomY) {
      doc.addPage();
      pageNum++;
      y = 20;
      addPageHeader();
    }
  };

  const addPageHeader = () => {
    doc.setFontSize(8);
    doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
    doc.setFont("helvetica", "italic");
    doc.text("Analyse iridologique - " + patientName, pageWidth / 2, 10, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.text("Page " + pageNum, pageWidth - margin, 10, { align: "right" });
    
    // Draw a subtle line below header
    doc.setDrawColor(colors.background.r, colors.background.g, colors.background.b);
    doc.setLineWidth(0.5);
    doc.line(margin, 14, pageWidth - margin, 14);
  };

  const addSectionTitle = (title: string) => {
    checkPageBreak(25);
    y += 6;
    doc.setFillColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.roundedRect(margin, y - 5, contentWidth, 10, 2, 2, "F");
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(title, margin + 5, y + 1);
    y += 14;
  };

  const addSubSection = (title: string) => {
    checkPageBreak(15);
    y += 2;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(title, margin, y);
    y += 8;
  };

  const addText = (text: string, indent: number = 0) => {
    const lines = doc.splitTextToSize(text, contentWidth - indent);
    checkPageBreak(lines.length * lineHeight + 4);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(lines, margin + indent, y);
    y += lines.length * lineHeight + 3;
  };

  const addLabelValue = (label: string, value: string | null | undefined, indent: number = 0) => {
    if (!value || !value.trim()) return;
    checkPageBreak(12);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(label + " :", margin + indent, y);
    
    const labelWidth = doc.getTextWidth(label + " : ");
    doc.setFont("helvetica", "normal");
    doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
    const lines = doc.splitTextToSize(value, contentWidth - indent - labelWidth - 8);
    doc.text(lines, margin + indent + labelWidth, y);
    y += Math.max(lines.length * lineHeight, lineHeight) + 4;
  };

  const addMultilineField = (label: string, value: string | null | undefined) => {
    if (!value || !value.trim()) return;
    
    const lines = doc.splitTextToSize(value, contentWidth - 14);
    const boxHeight = lines.length * lineHeight + 8;
    
    checkPageBreak(boxHeight + (label ? 10 : 4));
    
    if (label) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
      doc.text(label + " :", margin + 2, y);
      y += 7;
    }
    
    doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
    doc.roundedRect(margin + 2, y - 3, contentWidth - 4, boxHeight, 2, 2, "F");
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
    doc.text(lines, margin + 6, y + 2);
    y += boxHeight + 6;
  };

  const addBadges = (badges: string[], badgeColor?: { r: number; g: number; b: number }) => {
    if (badges.length === 0) return;
    checkPageBreak(18);
    
    let xPos = margin + 2;
    let currentLineY = y;
    const color = badgeColor || colors.primary;
    const badgeHeight = 8;
    
    badges.forEach((badge) => {
      const badgeWidth = doc.getTextWidth(badge) + 12;
      if (xPos + badgeWidth > pageWidth - margin) {
        currentLineY += badgeHeight + 4;
        xPos = margin + 2;
        checkPageBreak(badgeHeight + 4);
      }
      
      doc.setFillColor(color.r, color.g, color.b);
      doc.roundedRect(xPos, currentLineY - 4, badgeWidth, badgeHeight, 3, 3, "F");
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text(badge, xPos + 6, currentLineY + 1);
      xPos += badgeWidth + 6;
    });
    y = currentLineY + badgeHeight + 8;
  };

  const addBulletList = (items: string[], indent: number = 5) => {
    items.forEach((item) => {
      const lines = doc.splitTextToSize("- " + item, contentWidth - indent - 8);
      checkPageBreak(lines.length * lineHeight + 3);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
      doc.text(lines, margin + indent, y);
      y += lines.length * lineHeight + 2;
    });
    y += 2;
  };

  // ==================== HEADER (identique au Bilan de Vitalité : logo centré) ====================
  const headerHeight = 62;
  doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
  doc.rect(0, 0, pageWidth, headerHeight, "F");

  // Bande dorée en bas du header
  doc.setFillColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
  doc.rect(0, headerHeight, pageWidth, 2, "F");

  // Logo centré
  const logoSize = 32;
  const logoY = 5;
  try {
    doc.addImage(logoImage, "PNG", pageWidth / 2 - logoSize / 2, logoY, logoSize, logoSize);
  } catch (error) {
    console.log("Logo non charge");
  }

  // Nom de marque centré : "REGARD" en or, "DE NATURE" en vert
  doc.setTextColor(colors.accent.r, colors.accent.g, colors.accent.b);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(BRAND.name.line1, pageWidth / 2, logoY + logoSize + 7, { align: "center" });

  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text(BRAND.name.line2, pageWidth / 2, logoY + logoSize + 14, { align: "center" });

  // Baseline centrée
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text(`${BRAND.baseline.parts.text1} & ${BRAND.baseline.parts.text2}`, pageWidth / 2, logoY + logoSize + 20, { align: "center" });

  y = headerHeight + 8;

  // ==================== TITLE ====================
  // Séparateur décoratif haut
  doc.setDrawColor(colors.accent.r, colors.accent.g, colors.accent.b);
  doc.setLineWidth(0.5);
  doc.line(margin + 20, y, pageWidth - margin - 20, y);
  y += 7;

  doc.setFontSize(15);
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.setFont("helvetica", "bold");
  doc.text("BILAN IRIDOLOGIQUE COMPLET", pageWidth / 2, y, { align: "center" });
  y += 6;

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.accent.r, colors.accent.g, colors.accent.b);
  doc.text("Lecture du terrain et accompagnement personnalise", pageWidth / 2, y, { align: "center" });
  y += 6;

  // Séparateur décoratif bas
  doc.setDrawColor(colors.accent.r, colors.accent.g, colors.accent.b);
  doc.setLineWidth(0.5);
  doc.line(margin + 20, y, pageWidth - margin - 20, y);
  y += 8;

  // ==================== PRACTITIONER INFO (like Bilan de Vitalité) ====================
  if (practitionerInfo?.practitioner_name) {
    // Nom - Titre sur une ligne
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(
      `${practitionerInfo.practitioner_name} - Naturopathe`,
      pageWidth / 2, y, { align: "center" }
    );
    y += 6;

    // Adresse sur sa propre ligne
    if (practitionerInfo.address) {
      // Split address on comma if it's a single line to put on two lines
      const addressParts = practitionerInfo.address.split(",").map(s => s.trim());
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
      if (addressParts.length >= 2) {
        doc.text(addressParts[0], pageWidth / 2, y, { align: "center" });
        y += 5;
        doc.text(addressParts.slice(1).join(", "), pageWidth / 2, y, { align: "center" });
        y += 5;
      } else {
        doc.text(practitionerInfo.address, pageWidth / 2, y, { align: "center" });
        y += 5;
      }
    }

    // Tel + Email sur une ligne
    const contactLine2: string[] = [];
    if (practitionerInfo.phone) contactLine2.push(`Tel : ${practitionerInfo.phone}`);
    if (practitionerInfo.email) contactLine2.push(`Email : ${practitionerInfo.email}`);
    if (contactLine2.length > 0) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
      const contactText = contactLine2.join("   |   ");
      const contactLines = doc.splitTextToSize(contactText, contentWidth - 20);
      doc.text(contactLines, pageWidth / 2, y, { align: "center" });
      y += contactLines.length * 5;
    }
    y += 5;
  }

  // ==================== PATIENT INFO ====================
  doc.setFillColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
  doc.roundedRect(margin, y, contentWidth, 20, 3, 3, "F");

  const consultType =
    formData.consultation_type === "iridology_only"
      ? "Iridologie seule"
      : "Iridologie + Naturopathie";
  const eyeLabel =
    formData.eye_observed === "right"
      ? "Oeil droit"
      : formData.eye_observed === "left"
      ? "Oeil gauche"
      : "Les deux yeux";

  // Ligne 1 : Consultant + Type
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text("Consultant :", margin + 5, y + 7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
  doc.text(patientName, margin + 28, y + 7);

  doc.setFont("helvetica", "bold");
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text("Type :", pageWidth / 2 + 5, y + 7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
  doc.text(consultType, pageWidth / 2 + 18, y + 7);

  // Ligne 2 : Oeil observé
  doc.setFont("helvetica", "bold");
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text("Oeil observe :", margin + 5, y + 15);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
  doc.text(eyeLabel, margin + 35, y + 15);

  y += 28;

  // ==================== COMPARISON IMAGES SECTION ====================
  if (rightEye?.imageData || leftEye?.imageData) {
    addSectionTitle("Images de l'iris (Comparaison)");

    const imgWidth = 70;
    const imgHeight = 70;
    const spacing = 15;
    const startX = (pageWidth - imgWidth * 2 - spacing) / 2;

    checkPageBreak(imgHeight + 30);

    const rightZones = selectedZones.filter((z) => z.eyeSide === "D");
    const leftZones = selectedZones.filter((z) => z.eyeSide === "G");

    const rightImg = rightEye?.imageData && rightEye.calibration
      ? await renderEyeWithSelectedZones(rightEye.imageData, rightEye.calibration, rightZones)
      : rightEye?.imageData;
    const leftImg = leftEye?.imageData && leftEye.calibration
      ? await renderEyeWithSelectedZones(leftEye.imageData, leftEye.calibration, leftZones)
      : leftEye?.imageData;

    // Draw labels
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text("Oeil Droit", startX + imgWidth / 2, y, { align: "center" });
    doc.text("Oeil Gauche", startX + imgWidth + spacing + imgWidth / 2, y, { align: "center" });
    y += 6;

    const drawEyeBox = async (imgDataUrl: string | null | undefined, x: number) => {
      if (!imgDataUrl) {
        doc.setFillColor(240, 240, 240);
        doc.roundedRect(x, y, imgWidth, imgHeight, 3, 3, "F");
        doc.setFontSize(8);
        doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
        doc.text("Non disponible", x + imgWidth / 2, y + imgHeight / 2, { align: "center" });
        return;
      }

      try {
        const resolved = await resolveImageToDataUrl(imgDataUrl);
        if (!resolved) throw new Error("No image");
        doc.addImage(resolved, dataUrlFormat(resolved) as any, x, y, imgWidth, imgHeight);
      } catch (error) {
        doc.setFillColor(240, 240, 240);
        doc.roundedRect(x, y, imgWidth, imgHeight, 3, 3, "F");
        doc.setFontSize(8);
        doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
        doc.text("Image non disponible", x + imgWidth / 2, y + imgHeight / 2, { align: "center" });
      }
    };

    await drawEyeBox(rightImg, startX);
    await drawEyeBox(leftImg, startX + imgWidth + spacing);

    y += imgHeight + 10;
  }

  // Photos section removed (duplicates "Images de l'iris")

  // ==================== SELECTED ZONES SECTION ====================
  if (selectedZones.length > 0) {
    addSectionTitle("Zones selectionnees pour l'analyse");

    const rightZones = selectedZones.filter((z) => z.eyeSide === "D");
    const leftZones = selectedZones.filter((z) => z.eyeSide === "G");

    const drawZoneDetails = (zones: SelectedZoneWithDetails[], eyeLabel: string) => {
      if (zones.length === 0) return;

      addSubSection(eyeLabel);

      zones.forEach((entry, idx) => {
        // Calculate approximate height for this zone block
        const descLines = doc.splitTextToSize(entry.zone.description_iridologie || "", contentWidth - 18);
        const actionsCount = entry.zone.pistes_action?.length || 0;
        const alertsCount = entry.zone.signaux_alerte?.length || 0;
        const hasObs = !!entry.observation;
        const estimatedHeight = 20 + descLines.length * lineHeight + actionsCount * 8 + alertsCount * 8 + (hasObs ? 25 : 0);
        
        checkPageBreak(Math.min(estimatedHeight, 80));

        // Zone title with color bullet
        const zoneColor = entry.zone.color || "#3B82F6";
        doc.setFillColor(
          parseInt(zoneColor.slice(1, 3), 16) || 100,
          parseInt(zoneColor.slice(3, 5), 16) || 100,
          parseInt(zoneColor.slice(5, 7), 16) || 100
        );
        doc.circle(margin + 4, y, 3, "F");

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
        doc.text(entry.zone.label, margin + 10, y + 1);
        // Calculate label width BEFORE changing font
        const zoneLabelWidth = doc.getTextWidth(entry.zone.label);

        // Ring label - display on new line to avoid overlap
        const ringLabel = entry.zone.ring === "inner" ? "Couronne interne" : "Couronne externe";
        doc.setFontSize(8);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
        doc.text(ringLabel, margin + 10, y + 6);

        // Severity badge on the right
        if (entry.severityLevel) {
          const sevColor = severityColors[entry.severityLevel] || severityColors.normal;
          const sevLabel = priorityLabels[entry.severityLevel] || "Normal";
          doc.setFillColor(sevColor.r, sevColor.g, sevColor.b);
          const badgeWidth = doc.getTextWidth(sevLabel) + 12;
          doc.roundedRect(pageWidth - margin - badgeWidth, y - 4, badgeWidth, 8, 2, 2, "F");
          doc.setFontSize(7);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(255, 255, 255);
          doc.text(sevLabel, pageWidth - margin - badgeWidth + 6, y + 1);
        }

        y += 16;

        // Description iridologique box
        if (entry.zone.description_iridologie) {
          const descHeight = descLines.length * lineHeight + 10;
          checkPageBreak(descHeight + 5);
          
          doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
          doc.roundedRect(margin + 5, y - 3, contentWidth - 10, descHeight, 2, 2, "F");

          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
          doc.text("Lecture iridologique :", margin + 8, y + 2);
          y += 8;

          doc.setFont("helvetica", "normal");
          doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
          doc.text(descLines, margin + 8, y);
          y += descLines.length * lineHeight + 6;
        }

        // Pistes d'accompagnement
        if (entry.zone.pistes_action && entry.zone.pistes_action.length > 0) {
          const actionsHeight = entry.zone.pistes_action.length * 7 + 10;
          checkPageBreak(actionsHeight);
          
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
          doc.text("Pistes d'accompagnement :", margin + 8, y);
          y += 7;

          entry.zone.pistes_action.forEach((action) => {
            const actionLines = doc.splitTextToSize("- " + action, contentWidth - 28);
            checkPageBreak(actionLines.length * lineHeight + 2);
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
            doc.text(actionLines, margin + 12, y);
            y += actionLines.length * lineHeight + 2;
          });
          y += 4;
        }

        // Signaux d'alerte box
        if (entry.zone.signaux_alerte && entry.zone.signaux_alerte.length > 0) {
          const alertHeight = entry.zone.signaux_alerte.length * 7 + 14;
          checkPageBreak(alertHeight);
          
          doc.setFillColor(255, 250, 240);
          doc.roundedRect(margin + 5, y - 2, contentWidth - 10, alertHeight, 2, 2, "F");

          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(200, 120, 50);
          doc.text("Points de vigilance :", margin + 8, y + 3);
          y += 9;

          entry.zone.signaux_alerte.forEach((signal) => {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(160, 100, 40);
            const signalLines = doc.splitTextToSize("- " + signal, contentWidth - 28);
            doc.text(signalLines, margin + 10, y);
            y += signalLines.length * lineHeight + 2;
          });
          y += 5;
        }

        // Personal observation box
        if (entry.observation) {
          const obsLines = doc.splitTextToSize(entry.observation, contentWidth - 28);
          const obsHeight = obsLines.length * lineHeight + 14;
          checkPageBreak(obsHeight);
          
          doc.setFillColor(240, 250, 245);
          doc.roundedRect(margin + 5, y - 2, contentWidth - 10, obsHeight, 2, 2, "F");

          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
          doc.text("Mes observations :", margin + 8, y + 3);
          y += 9;

          doc.setFont("helvetica", "normal");
          doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
          doc.text(obsLines, margin + 10, y);
          y += obsLines.length * lineHeight + 6;
        }

        y += 10;
      });
    };

    drawZoneDetails(rightZones, "Oeil Droit");
    drawZoneDetails(leftZones, "Oeil Gauche");
  }

  // ==================== ZONE OBSERVATIONS SECTION ====================
  if (zoneObservations.length > 0) {
    addSectionTitle("Observations enregistrees");

    const rightObs = zoneObservations.filter((o) => o.eye_side === "D");
    const leftObs = zoneObservations.filter((o) => o.eye_side === "G");

    const drawObsList = (obs: ZoneObservation[], label: string) => {
      if (obs.length === 0) return;
      addSubSection(label);

      obs.forEach((observation) => {
        const noteLines = observation.observation_notes 
          ? doc.splitTextToSize(observation.observation_notes, contentWidth - 18)
          : [];
        const blockHeight = 10 + noteLines.length * lineHeight;
        checkPageBreak(blockHeight);

        const sevColor = severityColors[observation.severity_level || "normal"] || severityColors.normal;
        doc.setFillColor(sevColor.r, sevColor.g, sevColor.b);
        doc.circle(margin + 4, y, 2.5, "F");

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
        doc.text(observation.zone_label, margin + 10, y + 1);
        // Calculate label width BEFORE changing font
        const obsLabelWidth = doc.getTextWidth(observation.zone_label);

        const ringLabel = observation.zone_ring === "inner" ? "(Int.)" : "(Ext.)";
        doc.setFontSize(7);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
        doc.text(ringLabel, margin + 12 + obsLabelWidth, y + 1);

        y += 8;

        if (observation.observation_notes && noteLines.length > 0) {
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
          doc.text(noteLines, margin + 10, y);
          y += noteLines.length * lineHeight + 5;
        }
      });
      y += 4;
    };

    drawObsList(rightObs, "Oeil Droit");
    drawObsList(leftObs, "Oeil Gauche");
  }

  // ==================== OBSERVATIONS IRIDOLOGIQUES ====================
  if (observationsData) {
    addSectionTitle("Observations iridologiques");
    
    if (observationsData.elements_remarquables) {
      addMultilineField("Elements remarquables", observationsData.elements_remarquables);
    }
    
    const impressions: string[] = [];
    if (observationsData.aspect_general) impressions.push(`Aspect: ${observationsData.aspect_general === 'harmonique' ? 'Harmonique' : 'Dysharmonique'}`);
    if (observationsData.couleur) impressions.push(`Couleur: ${observationsData.couleur === 'franche' ? 'Franche' : 'Heterogene'}`);
    if (observationsData.trame) impressions.push(`Trame: ${observationsData.trame === 'compacte' ? 'Compacte' : 'Clairsemee'}`);
    if (observationsData.pupilles) impressions.push(`Pupilles: ${observationsData.pupilles === 'rondes' ? 'Rondes' : 'Deformees'}`);
    if (impressions.length > 0) addBadges(impressions);
    
    const constitutionLabels: Record<string, string> = {
      lymphatique: 'Lymphatique fibrillaire',
      hematogene: 'Hematogene',
      mixte_hepatique: 'Mixte hepatique',
      mixte_pancreatique: 'Mixte pancreatique',
    };
    if (observationsData.constitution) {
      addLabelValue("Constitution", constitutionLabels[observationsData.constitution] || observationsData.constitution);
    }
    
    if (observationsData.trame_od || observationsData.trame_og) {
      addLabelValue("Trame", `OD: ${observationsData.trame_od || '-'}/5 | OG: ${observationsData.trame_og || '-'}/5`);
    }
    
    const intoxination: string[] = [];
    if (observationsData.acide_oxalique) intoxination.push("Acide oxalique");
    if (observationsData.acide_pyruvique) intoxination.push("Acide pyruvique");
    if (observationsData.acide_urique) intoxination.push("Acide urique");
    if (observationsData.anneau_lipidique) intoxination.push("Anneau lipidique");
    if (observationsData.anneau_gerontoxon) intoxination.push("Gerontoxon");
    if (intoxination.length > 0) {
      addSubSection("Intoxination");
      addBadges(intoxination, { r: 200, g: 120, b: 50 });
    }
    
    const signes: string[] = [];
    if (observationsData.radii_solari) signes.push("Radii Solari");
    if (observationsData.arcs_cercle) signes.push("Arcs de cercle");
    if (observationsData.vacuoles) signes.push("Vacuoles");
    if (observationsData.lacunes) signes.push("Lacunes");
    if (observationsData.cryptes) signes.push("Cryptes");
    if (signes.length > 0) {
      addSubSection("Signes iriens");
      addBadges(signes);
    }
    
    if (observationsData.conclusions_psy) {
      addMultilineField("Conclusions psycho-emotionnelles", observationsData.conclusions_psy);
    }
  }

  // ==================== TERRAIN & ANALYSIS ====================
  const terrains: string[] = [];
  if (formData.terrain_nervous) terrains.push("Nerveux");
  if (formData.terrain_digestive) terrains.push("Digestif");
  if (formData.terrain_circulatory) terrains.push("Circulatoire");
  if (formData.terrain_eliminatory) terrains.push("Eliminatoire");
  if (formData.terrain_mixed) terrains.push("Mixte");

  if (terrains.length > 0) {
    addSectionTitle("Terrain dominant");
    addBadges(terrains);
  }

  // ==================== VITALITY ====================
  if (formData.vitality_level) {
    addSectionTitle("Vitalite globale");

    const vitalityLabels: Record<string, string> = {
      good: "Bonne",
      average: "Moyenne",
      needs_support: "A soutenir",
    };
    const vitalityColors: Record<string, { r: number; g: number; b: number }> = {
      good: { r: 34, g: 139, b: 34 },
      average: { r: 218, g: 165, b: 32 },
      needs_support: { r: 255, g: 140, b: 0 },
    };

    const vitalityLabel = vitalityLabels[formData.vitality_level] || "Non renseigne";
    const vColor = vitalityColors[formData.vitality_level] || colors.primary;

    addBadges([vitalityLabel], vColor);

    if (formData.vitality_comments) {
      addMultilineField("Commentaires", formData.vitality_comments);
    }
  }

  // ==================== ADAPTATIONS ====================
  const adaptations: string[] = [];
  if (formData.adaptation_good) adaptations.push("Bonne");
  if (formData.adaptation_stress_sensitivity) adaptations.push("Sensibilite au stress");
  if (formData.adaptation_fatigue) adaptations.push("Fatigabilite possible");

  if (adaptations.length > 0) {
    addSectionTitle("Capacites d'adaptation");
    addBadges(adaptations);
  }

  // ==================== ZONES ====================
  if (formData.sensitivity_zones || formData.support_zones) {
    addSectionTitle("Zones observees");
    addMultilineField("Zones de sensibilite", formData.sensitivity_zones);
    addMultilineField("Zones a soutenir", formData.support_zones);
  }

  // ==================== OVERLOADS ====================
  const overloads: string[] = [];
  if (formData.overload_digestive) overloads.push("Digestive");
  if (formData.overload_nervous) overloads.push("Nerveuse");
  if (formData.overload_emotional) overloads.push("Emotionnelle");
  if (formData.overload_eliminatory) overloads.push("Eliminatoire");
  if (formData.overload_circulatory) overloads.push("Circulatoire");

  if (overloads.length > 0) {
    addSectionTitle("Signes de surcharge");
    addBadges(overloads, { r: 200, g: 120, b: 50 });
  }

  // ==================== SUPPORT AXES ====================
  const axes: string[] = [];
  if (formData.axis_hygiene) axes.push("Hygiene de vie");
  if (formData.axis_nutrition) axes.push("Ajustements alimentaires");
  if (formData.axis_plants) axes.push("Soutien par les plantes");
  if (formData.axis_stress) axes.push("Gestion du stress");
  if (formData.axis_seasonal) axes.push("Accompagnement saisonnier");

  if (axes.length > 0) {
    addSectionTitle("Axes d'accompagnement proposes");
    addBadges(axes);
  }

  // ==================== OBSERVATIONS ====================
  if (formData.additional_observations) {
    addSectionTitle("Observations complementaires");
    addMultilineField("", formData.additional_observations);
  }

  // ==================== RECOMMENDATIONS ====================
  if (recommendations.length > 0) {
    addSectionTitle("Recommandations personnalisees");

    recommendations.forEach((rec) => {
      const descLines = rec.description ? doc.splitTextToSize(rec.description, contentWidth - 12) : [];
      const blockHeight = 15 + descLines.length * lineHeight;
      checkPageBreak(blockHeight);

      // Category badge
      doc.setFillColor(colors.accent.r, colors.accent.g, colors.accent.b);
      const catWidth = doc.getTextWidth(rec.category) + 12;
      doc.roundedRect(margin + 2, y - 3, catWidth, 8, 2, 2, "F");
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text(rec.category, margin + 8, y + 2);

      // Title
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
      doc.text(rec.title, margin + catWidth + 10, y + 1);
      y += 12;

      // Description
      if (rec.description && descLines.length > 0) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
        doc.text(descLines, margin + 6, y);
        y += descLines.length * lineHeight + 6;
      }
      
      y += 4;
    });
  }

  // ==================== ORIENTATION BOX ====================
  checkPageBreak(40);
  y += 5;
  
  doc.setFillColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.roundedRect(margin, y, contentWidth, 24, 3, 3, "F");

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("Orientation de l'accompagnement", margin + 6, y + 8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const orientationText =
    "Cette analyse iridologique oriente vers un accompagnement naturopathique personnalise visant a soutenir l'equilibre global et la vitalite du consultant.";
  const orientationLines = doc.splitTextToSize(orientationText, contentWidth - 14);
  doc.text(orientationLines, margin + 6, y + 16);
  y += 32;

  // ==================== SIGNATURE SECTION ====================
  if (data.signatureUrl) {
    checkPageBreak(45);

    doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
    doc.roundedRect(margin, y, contentWidth, 40, 3, 3, "F");

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text("Signature du praticien", margin + 6, y + 8);

    try {
      const sigImg = new Image();
      sigImg.src = data.signatureUrl;
      await new Promise<void>((resolve) => {
        sigImg.onload = () => resolve();
        sigImg.onerror = () => resolve();
      });

      if (sigImg.complete && sigImg.naturalWidth > 0) {
        const sigMaxW = 50;
        const sigMaxH = 25;
        const sigRatio = sigImg.naturalWidth / sigImg.naturalHeight;
        let sigW = sigMaxW;
        let sigH = sigW / sigRatio;
        if (sigH > sigMaxH) {
          sigH = sigMaxH;
          sigW = sigH * sigRatio;
        }
        doc.addImage(data.signatureUrl, "PNG", margin + 6, y + 12, sigW, sigH);
      }
    } catch (e) {
      console.error("Error adding signature to PDF:", e);
    }

    y += 45;
  }

  // ==================== FOOTER ON ALL PAGES ====================
  const addFooter = (pageIndex: number) => {
    doc.setPage(pageIndex);
    const footerY = pageHeight - 22;

    doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
    doc.roundedRect(margin, footerY, contentWidth, 14, 2, 2, "F");

    doc.setFontSize(6);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
    const disclaimer =
      "L'iridologie est un outil d'observation du terrain et ne constitue pas un diagnostic medical. Cette approche ne remplace en aucun cas un suivi medical et s'inscrit dans une demarche de prevention et d'accompagnement naturel.";
    const disclaimerLines = doc.splitTextToSize(disclaimer, contentWidth - 10);
    doc.text(disclaimerLines, margin + 5, footerY + 4);

    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(BRAND.name.full, margin, pageHeight - 5);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
    doc.text("Document personnel et confidentiel", pageWidth - margin, pageHeight - 5, { align: "right" });
  };

  const totalPages = doc.internal.pages.length - 1;
  for (let i = 1; i <= totalPages; i++) {
    addFooter(i);
  }

  // Save
  const fileName = "bilan_iridologie_" + patientName.replace(/\s+/g, "_") + "_" + format(parseISO(sessionDate), "yyyy-MM-dd") + ".pdf";
  doc.save(fileName);
};
